import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider } from 'antd';
import { PlusCircleFilled } from '@ant-design/icons';
import { LoadingApiByIds } from '../../../Loading';
import { InfiniteScroll } from '../InfiniteScroll';
import { useStepperContext } from '../TariffCreationModal/context';
import { CreatePackageForm } from './CreatePackageForm';
import { PackageCard } from './PackageCard';
import { IPackageListPaginated, IPackagesList, IPostPackage, NotificationType } from '../../../../interfaces';
import { useTariffsPackages } from '../../../../hooks';
import { usePackageSelection } from './hooks';
import {
    addNotification,
    apiHandleErrorMessages,
    createTariffPackage,
    getTariffPackagesList,
} from '../../../../middleware';
import { makeSelectionLabel } from '../../../../instruments';
import { tB, tF, tT } from '../../../../translate';
import TariffPStyles from '../../m_styles.less';

const PackageSelectionComponent = ({
    packageType,
    packages,
    close,
    onPackageSelected,
    initialSelectedPackages,
}: {
    packageType: 'local' | 'roaming';
    packages: IPackagesList;
    close: () => void;
    onPackageSelected: (packages: number[]) => void;
    initialSelectedPackages: number[];
}) => {
    const {
        selectedPackages,
        isCreateMode,
        handlePackageToggle,
        handleSelectAll,
        handleCreatePackage,
        handleBackToSelect,
        isPackageSelected,
        canProceed,
    } = usePackageSelection({ packageType, packages, initialSelectedPackages });

    const { formData } = useStepperContext();
    const pageSize = 5;
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<any[]>([]);
    const [total, setTotal] = useState(0);

    const handleSelectPackages = () => {
        onPackageSelected(selectedPackages);
    };

    const fetchData = async (page: number, override?: boolean) => {
        const resp: IPackageListPaginated = (await getTariffPackagesList(
            { pageNumber: page, pageSize: pageSize, packageType, companyId: formData.companyId },
            true,
        )) as IPackageListPaginated;

        if (override) {
            setItems(resp.data);
        } else {
            setItems((prevList) => [...prevList, ...resp.data]);
        }

        setTotal(resp?.meta?.records_count || 0);
    };

    const handleSavePackage = async (packageData: IPostPackage) => {
        const { isError, resp } = await createTariffPackage(packageData);

        if (isError) {
            apiHandleErrorMessages(resp || {});

            return;
        }

        addNotification({
            type: NotificationType.SUCCESS,
            message: 'Created package successfully.',
            isUsersError: true,
        });

        await fetchData(1, true);

        handleBackToSelect();
    };

    useEffect(() => {
        fetchData(1);
    }, []);

    if (isCreateMode) {
        return (
            <CreatePackageForm
                packageType={packageType}
                onBack={handleBackToSelect}
                onSave={handleSavePackage}
                tariffCompanyId={formData.companyId}
            />
        );
    }

    const title = packageType === tF('local' ? 'Select Local Package' : 'Select Roaming Package');
    const description =
        packageType ===
        tF(
            'local'
                ? 'Choose an existing package or create a new one'
                : 'Choose multiple packages for roaming coverage',
        );

    return (
        <div>
            <div className={TariffPStyles.formContainer}>
                <div>
                    <h3 className={TariffPStyles.formTitle}>{title}</h3>
                    <p className={TariffPStyles.formDescription}>{description}</p>
                    {packageType === 'roaming' && (
                        <div className={TariffPStyles.packageSelectionInfo}>
                            {makeSelectionLabel('package', selectedPackages.length)}
                        </div>
                    )}
                </div>
                <div className={TariffPStyles.createActions}>
                    <Button onClick={handleCreatePackage}>
                        <PlusCircleFilled /> {tB('Create')}
                    </Button>
                    {packageType === 'roaming' && (
                        <Button onClick={handleSelectAll}>
                            {tB(selectedPackages.length === packages.length ? 'Deselect All' : 'Select All')}
                        </Button>
                    )}
                </div>
                <Divider />
                <div className={TariffPStyles.packageListContainer}>
                    <InfiniteScroll
                        pageSize={pageSize}
                        page={page}
                        fetchFunc={fetchData}
                        totalPackage={total}
                        currentPackages={items.length}
                        handleSetPage={setPage}
                    >
                        {items.map((pkg) => (
                            <PackageCard
                                key={pkg.id}
                                id={pkg.id}
                                packageInfo={pkg}
                                badgeName={tF(packageType === 'local' ? 'Local' : 'Roaming')}
                                isSelected={isPackageSelected(+pkg.id)}
                                onToggleSelect={handlePackageToggle}
                            />
                        ))}
                    </InfiniteScroll>
                </div>
                <div className={TariffPStyles.actionsContainer}>
                    <Button onClick={close}>{tB('Cancel')}</Button>
                    <Button type="primary" disabled={!canProceed} onClick={handleSelectPackages}>
                        {packageType === 'local'
                            ? tB('Select Package')
                            : `${tB('Select Packages')} (${selectedPackages.length})`}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const SelectedPackagesDisplay = ({
    packages,
    selectedPackageIds,
    packageType,
    onDelete,
}: {
    packages: IPackagesList;
    selectedPackageIds: number[];
    packageType: 'local' | 'roaming';
    onDelete: (id: number) => void;
}) => {
    const selectedPackages = packages.filter((pkg) => selectedPackageIds.includes(+pkg.id));

    if (selectedPackages.length === 0) return null;

    return (
        <div>
            <div style={{ marginBottom: 10 }}>
                <span>{makeSelectionLabel(`${packageType} package`, selectedPackages.length)}</span>
            </div>
            <div className={TariffPStyles.packageListContainer}>
                {selectedPackages.map((pkg) => (
                    <PackageCard
                        key={pkg.id}
                        id={pkg.id}
                        packageInfo={pkg}
                        badgeName={tF(packageType === 'local' ? 'Local' : 'Roaming')}
                        isSelected={false}
                        showEditOption={true}
                        onDelete={onDelete}
                    />
                ))}
            </div>
        </div>
    );
};

export const PackageStep = observer(() => {
    const { formData, updateFormData } = useStepperContext();
    const packages = useTariffsPackages();

    const [isOpenSelectLocal, setIsOpenSelectLocal] = useState(false);
    const [isOpenSelectRoaming, setIsOpenSelectRoaming] = useState(false);

    const packageIds = formData.tariffPackageIds.map((id) => id);
    const selectedLocalPackages = packageIds.filter((id) => {
        const pkg = packages.find((p) => +p.id === +id);
        return pkg?.packageType === 'local';
    });
    const selectedRoamingPackages = packageIds.filter((id) => {
        const pkg = packages.find((p) => +p.id === +id);

        return pkg?.packageType !== 'local';
    });

    useEffect(() => {
        getTariffPackagesList({ companyId: formData.companyId }).then();
    }, [JSON.stringify(formData.tariffPackageIds)]);

    const openSelectLocal = () => setIsOpenSelectLocal(true);
    const closeSelectLocal = () => setIsOpenSelectLocal(false);
    const openSelectRoaming = () => setIsOpenSelectRoaming(true);
    const closeSelectRoaming = () => setIsOpenSelectRoaming(false);

    const updateTariffPackageIds = (local: number[], roaming: number[]) => {
        const allIds = [...local, ...roaming].map((id) => Number(id));
        updateFormData({ tariffPackageIds: allIds });
    };

    const handleLocalPackageSelected = (packageIds: number[]) => {
        updateTariffPackageIds(packageIds, selectedRoamingPackages);
        closeSelectLocal();
    };

    const handleRoamingPackagesSelected = (packageIds: number[]) => {
        updateTariffPackageIds(selectedLocalPackages, packageIds);
        closeSelectRoaming();
    };

    const onDeleteLocal = (id: number) => {
        const newLocal = selectedLocalPackages.filter((pkgId) => +pkgId !== +id);
        updateTariffPackageIds(newLocal, selectedRoamingPackages);
    };

    const onDeleteRoaming = (id: number) => {
        const newRoaming = selectedRoamingPackages.filter((pkgId) => +pkgId !== +id);
        updateTariffPackageIds(selectedLocalPackages, newRoaming);
    };

    const localPackages = packages.filter((value) => value.packageType === 'local');
    const roamingPackages = packages.filter((value) => value.packageType === 'extra');

    return (
        <div className={TariffPStyles.packageStep}>
            <div className={TariffPStyles.packageSectionHeader}>
                <div>
                    <h3 className={TariffPStyles.formTitle}>{tB('Local Packages')}</h3>
                    <p className={TariffPStyles.packageSectionInfo}>{tT('Single package for local network usage')}</p>
                </div>
                {!isOpenSelectLocal && (
                    <Button onClick={openSelectLocal}>
                        <PlusCircleFilled /> {tB(selectedLocalPackages.length > 0 ? 'Change' : 'Select/Create')}
                    </Button>
                )}
            </div>
            {isOpenSelectLocal && (
                <PackageSelectionComponent
                    packageType="local"
                    packages={localPackages}
                    close={closeSelectLocal}
                    onPackageSelected={handleLocalPackageSelected}
                    initialSelectedPackages={selectedLocalPackages}
                />
            )}
            {!isOpenSelectLocal && selectedLocalPackages.length > 0 && (
                <SelectedPackagesDisplay
                    packages={packages}
                    selectedPackageIds={selectedLocalPackages}
                    packageType="local"
                    onDelete={onDeleteLocal}
                />
            )}
            <Divider />
            <div className={TariffPStyles.packageSectionHeader}>
                <div>
                    <h3 className={TariffPStyles.formTitle}>Roaming Packages</h3>
                    <p className={TariffPStyles.packageSectionInfo}>Multiple packages for roaming usage</p>
                </div>
                {!isOpenSelectRoaming && (
                    <Button onClick={openSelectRoaming}>
                        <PlusCircleFilled /> {selectedRoamingPackages.length > 0 ? 'Change' : 'Select/Create'}
                    </Button>
                )}
            </div>
            {isOpenSelectRoaming && (
                <PackageSelectionComponent
                    packageType="roaming"
                    packages={roamingPackages}
                    close={closeSelectRoaming}
                    onPackageSelected={handleRoamingPackagesSelected}
                    initialSelectedPackages={selectedRoamingPackages}
                />
            )}
            {!isOpenSelectRoaming && selectedRoamingPackages.length > 0 && (
                <SelectedPackagesDisplay
                    packages={packages}
                    selectedPackageIds={selectedRoamingPackages}
                    packageType="roaming"
                    onDelete={onDeleteRoaming}
                />
            )}
            <LoadingApiByIds idSpinners={['GET_TARIFF_PACKAGES']} />
        </div>
    );
});
